import React, { useState, useEffect, useRef } from "react";
import { userService } from "../../../_services";
import { FormDialog } from "@/components/ui/form-dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, FileText, CheckCircle, AlertCircle } from "lucide-react";

/**
 * Edit Pricing Sheet Modal
 * Modernized with shadcn Dialog and file upload
 */
function EditSheet({ hideModal, sheetData, changeInSheet }) {
    const [userId, setUserId] = useState('');
    const [userName, setUserName] = useState('');
    const [sheetTitle, setSheetTitle] = useState('');
    const [fileId, setFileId] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [userId, userName] = Usermeta_Data.split(':');
        setUserId(userId);
        setUserName(userName);

        if (sheetData) {
            setSheetTitle(sheetData.fileName);
            setFileId(sheetData.fileId);
        }
    }, [sheetData]);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setFormError('');
            setFormSuccess('');
        }
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    const handleSubmit = async () => {
        if (!fileInputRef.current || !fileInputRef.current.files || fileInputRef.current.files.length === 0) {
            setFormError("Please select a valid file.");
            return;
        }

        const file = fileInputRef.current.files[0];
        const fileType = file.type;
        const fileSize = file.size;

        // Validation
        const allowedTypes = ["application/pdf"];
        if (!allowedTypes.includes(fileType)) {
            setFormError("Invalid file type. Only PDF file is allowed.");
            return;
        }

        const maxSize = 5 * 1024 * 1024; // 5MB
        if (fileSize > maxSize) {
            setFormError("File size exceeds the 5MB limit.");
            return;
        }

        setLoading(true);
        setFormError('');
        setFormSuccess('');

        try {
            const formData = new FormData();
            formData.append("userId", userId);
            formData.append("userName", userName);
            formData.append("fileId", fileId);
            formData.append("file", file);
            formData.append("filetype", file.type.replace('application/', ''));

            const postdata = await userService.EditPricingSheet(formData);

            if (postdata && postdata.length > 0) {
                if (postdata[0].code === '1') {
                    setFormSuccess(postdata[0].message);
                    changeInSheet();
                    setTimeout(() => {
                        hideModal();
                    }, 2000);
                } else {
                    setFormError(postdata[0].message);
                }
            } else {
                setFormError('Error uploading file.');
            }
        } catch (error) {
            setFormError("Error uploading file.");
            console.error('Error editing pricing sheet:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        hideModal();
    };

    return (
        <FormDialog
            open={true}
            onOpenChange={handleClose}
            title={`Edit Sheet - ${sheetTitle}`}
            description="Upload a new PDF file to replace the current pricing sheet (Max 5MB)"
            onSubmit={handleSubmit}
            onCancel={handleClose}
            submitText="Update Sheet"
            loading={loading}
            disabled={!selectedFile}
            maxWidth="md"
        >
            <div className="space-y-4">
                {/* File Upload Area */}
                <div
                    onClick={triggerFileInput}
                    className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-accent transition-colors"
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        id="file_upload"
                        name="file_upload"
                        className="hidden"
                        accept="application/pdf"
                        onChange={handleFileChange}
                    />
                    <div className="flex flex-col items-center gap-2">
                        {selectedFile ? (
                            <>
                                <CheckCircle className="h-12 w-12 text-green-500" />
                                <p className="text-primary font-medium">File Selected</p>
                                <p className="text-sm text-primary/70">{selectedFile.name}</p>
                                <p className="text-xs text-primary/50">
                                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                            </>
                        ) : (
                            <>
                                <Upload className="h-12 w-12 text-primary/40" />
                                <p className="text-primary font-medium">Click to upload PDF</p>
                                <p className="text-sm text-primary/70">PDF files only (Max 5MB)</p>
                            </>
                        )}
                    </div>
                </div>

                {/* Error Message */}
                {formError && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{formError}</AlertDescription>
                    </Alert>
                )}

                {/* Success Message */}
                {formSuccess && (
                    <Alert variant="success">
                        <CheckCircle className="h-4 w-4" />
                        <AlertDescription>{formSuccess}</AlertDescription>
                    </Alert>
                )}
            </div>
        </FormDialog>
    );
}

export default EditSheet;
